import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import api from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { UPSELL_COPY, UPSELL_MICROCOPY } from "../lib/upsell";
import { StandardPriceBlock, StandardCta, useStandardPlan } from "./StandardUpsell";
import {
    SignOut, House, Plus, Buildings, Scales, Broadcast, ShieldCheck, SunDim, Moon,
    List, X, Archive, Crown, UserCircle, UsersThree, Coins, ChartLineUp, LockSimple, FunnelSimple,
} from "@phosphor-icons/react";
import { LEGAL_LINKS } from "../lib/legal";

const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: House },
    { to: "/radar", label: "Radar Opportunità", icon: Broadcast },
    { to: "/new", label: "Nuova analisi", icon: Plus },
    { to: "/compare", label: "Confronta", icon: Scales },
    { to: "/archive", label: "Archivio", icon: Archive },
];

let quotaCache = null;

// Routes still reachable when no paid plan is active:
// the user can ONLY purchase a plan (and complete the payment flow).
const PLAN_LOCK_ALLOWED = ["/piano", "/payment/success", "/payment/cancel"];

function PlanLockOverlay({ onLogout }) {
    const navigate = useNavigate();
    const std = useStandardPlan();
    useEffect(() => {
        trackEvent("paywall_viewed", "plan_required");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/65 backdrop-blur-[6px]" data-testid="plan-lock-overlay">
            <div className="w-full max-w-md rounded-2xl border border-[#7C3AED]/40 bg-gradient-to-b from-[#141C33] to-[#0E1424] p-7 text-center shadow-[0_24px_80px_rgba(0,0,0,0.5)]">
                <div className="w-12 h-12 mx-auto rounded-2xl bg-[#7C3AED]/20 text-[#A78BFA] flex items-center justify-center mb-4">
                    <LockSimple size={24} weight="fill" />
                </div>
                <h2 className="font-display-black text-xl text-white" data-testid="plan-lock-title">
                    Attiva un piano per continuare.
                </h2>
                <p className="text-sm text-slate-300 mt-2 leading-relaxed">
                    {UPSELL_COPY.plan_required.body}
                </p>
                <div className="mt-4 flex justify-center"><StandardPriceBlock std={std} dark /></div>
                <div className="mt-5">
                    <StandardCta variant="plan_required" source="plan_required" className="w-full" />
                </div>
                <div className="mt-2.5 flex items-center justify-between">
                    <span className="text-[11px] text-slate-400">{UPSELL_MICROCOPY}</span>
                    <button data-testid="plan-lock-upgrade-cta" onClick={() => navigate("/piano")}
                            className="text-[11px] text-slate-300 underline hover:text-white">
                        Confronta i piani
                    </button>
                </div>
                <button
                    data-testid="plan-lock-logout"
                    onClick={onLogout}
                    className="mt-4 w-full h-9 rounded-xl border border-white/10 text-slate-300 text-xs font-semibold hover:text-white hover:border-white/25 transition-colors flex items-center justify-center gap-2"
                >
                    <SignOut size={14} weight="bold" /> Esci
                </button>
            </div>
        </div>
    );
}

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const { setTheme, resolvedTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [quota, setQuota] = useState(quotaCache);

    useEffect(() => {
        api.get("/account/quota").then(({ data }) => { quotaCache = data; setQuota(data); }).catch(() => {});
    }, [location.pathname]);

    const doLogout = async () => {
        quotaCache = null;
        setQuota(null);
        await logout();
        navigate("/login");
    };

    const isAdmin = user?.role === "admin";
    const firstName = (user?.full_name || "").split(" ")[0] || "investitore";
    const planLabel = quota?.plan_label || quota?.plan || "";
    const noPlan = Boolean(quota?.requires_plan);
    const planText = noPlan ? "Nessun piano attivo" : (planLabel ? `Piano ${planLabel}` : "");
    const trialLocked =
        quota?.plan === "free" &&
        !PLAN_LOCK_ALLOWED.includes(location.pathname);

    const navLinks = (mobile = false) => (
        <>
            {NAV.map((item) => {
                const active = location.pathname === item.to;
                const Icon = item.icon;
                return (
                    <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => mobile && setMobileOpen(false)}
                        data-testid={`${mobile ? "mobile-" : ""}nav-${item.label.toLowerCase().split(" ")[0] === "radar" ? "radar" : item.label.toLowerCase().replace(/\s/g, "-")}`}
                        className={`px-4 h-11 flex items-center gap-3 text-[13px] font-semibold rounded-xl transition-all duration-200 ${
                            active
                                ? "text-white bg-gradient-to-r from-[#3B66F5] to-[#7C3AED] shadow-[0_4px_18px_rgba(79,90,245,0.35)]"
                                : "text-slate-300 hover:text-white hover:bg-white/[0.05]"
                        }`}
                    >
                        <Icon size={18} weight={active ? "fill" : "regular"} />
                        {item.label}
                    </Link>
                );
            })}
            {isAdmin && (
                <>
                    <div className="h-px bg-white/[0.07] my-3 mx-2" />
                    <Link
                        to="/admin"
                        onClick={() => mobile && setMobileOpen(false)}
                        data-testid={`${mobile ? "mobile-" : ""}nav-admin`}
                        className={`px-4 h-11 flex items-center gap-3 text-[13px] font-semibold rounded-xl transition-all duration-200 ${
                            location.pathname === "/admin" || (location.pathname.startsWith("/admin") && !["/admin/utenti", "/admin/costi", "/admin/traffico", "/admin/funnel"].includes(location.pathname))
                                ? "text-white bg-gradient-to-r from-[#0891B2] to-[#3B66F5] shadow-[0_4px_18px_rgba(8,145,178,0.3)]"
                                : "text-slate-300 hover:text-white hover:bg-white/[0.05]"
                        }`}
                    >
                        <ShieldCheck size={18} weight="regular" className={location.pathname.startsWith("/admin") && !["/admin/utenti", "/admin/costi", "/admin/traffico", "/admin/funnel"].includes(location.pathname) ? "" : "text-[#22D3EE]"} />
                        Admin
                    </Link>
                    <Link
                        to="/admin/utenti"
                        onClick={() => mobile && setMobileOpen(false)}
                        data-testid={`${mobile ? "mobile-" : ""}nav-admin-utenti`}
                        className={`px-4 h-11 flex items-center gap-3 text-[13px] font-semibold rounded-xl transition-all duration-200 ${
                            location.pathname === "/admin/utenti"
                                ? "text-white bg-gradient-to-r from-[#0891B2] to-[#3B66F5] shadow-[0_4px_18px_rgba(8,145,178,0.3)]"
                                : "text-slate-300 hover:text-white hover:bg-white/[0.05]"
                        }`}
                    >
                        <UsersThree size={18} weight="regular" className={location.pathname === "/admin/utenti" ? "" : "text-[#22D3EE]"} />
                        Utenti
                    </Link>
                    <Link
                        to="/admin/costi"
                        onClick={() => mobile && setMobileOpen(false)}
                        data-testid={`${mobile ? "mobile-" : ""}nav-admin-costi`}
                        className={`px-4 h-11 flex items-center gap-3 text-[13px] font-semibold rounded-xl transition-all duration-200 ${
                            location.pathname === "/admin/costi"
                                ? "text-white bg-gradient-to-r from-[#0891B2] to-[#3B66F5] shadow-[0_4px_18px_rgba(8,145,178,0.3)]"
                                : "text-slate-300 hover:text-white hover:bg-white/[0.05]"
                        }`}
                    >
                        <Coins size={18} weight="regular" className={location.pathname === "/admin/costi" ? "" : "text-[#22D3EE]"} />
                        Costi AI
                    </Link>
                    <Link
                        to="/admin/traffico"
                        onClick={() => mobile && setMobileOpen(false)}
                        data-testid={`${mobile ? "mobile-" : ""}nav-admin-traffico`}
                        className={`px-4 h-11 flex items-center gap-3 text-[13px] font-semibold rounded-xl transition-all duration-200 ${
                            location.pathname === "/admin/traffico"
                                ? "text-white bg-gradient-to-r from-[#0891B2] to-[#3B66F5] shadow-[0_4px_18px_rgba(8,145,178,0.3)]"
                                : "text-slate-300 hover:text-white hover:bg-white/[0.05]"
                        }`}
                    >
                        <ChartLineUp size={18} weight="regular" className={location.pathname === "/admin/traffico" ? "" : "text-[#22D3EE]"} />
                        Traffico
                    </Link>
                    <Link
                        to="/admin/funnel"
                        onClick={() => mobile && setMobileOpen(false)}
                        data-testid={`${mobile ? "mobile-" : ""}nav-admin-funnel`}
                        className={`px-4 h-11 flex items-center gap-3 text-[13px] font-semibold rounded-xl transition-all duration-200 ${
                            location.pathname === "/admin/funnel"
                                ? "text-white bg-gradient-to-r from-[#0891B2] to-[#3B66F5] shadow-[0_4px_18px_rgba(8,145,178,0.3)]"
                                : "text-slate-300 hover:text-white hover:bg-white/[0.05]"
                        }`}
                    >
                        <FunnelSimple size={18} weight="regular" className={location.pathname === "/admin/funnel" ? "" : "text-[#22D3EE]"} />
                        Funnel
                    </Link>
                </>
            )}
        </>
    );

    const planCard = (mobile = false) => (
        <div className="space-y-3">
            {quota && !quota.unlimited && (
                <div className="rounded-2xl border border-white/[0.09] bg-gradient-to-b from-[#141C33] to-[#0E1424] p-4" data-testid={`${mobile ? "mobile-" : ""}sidebar-quota`}>
                    <div className="flex items-center gap-2 mb-2">
                        <Crown size={14} weight="fill" className="text-amber-400" />
                        <span className="text-xs font-bold text-white">{planText}</span>
                    </div>
                    <div className="text-[11px] text-slate-400 mb-1">Analisi utilizzate</div>
                    <div className="font-mono-tight text-lg font-bold text-white mb-2">{quota.used} / {quota.limit}</div>
                    <div className="h-1.5 w-full bg-white/[0.08] rounded-full overflow-hidden mb-2.5">
                        <div
                            className={`h-full rounded-full transition-all duration-700 ${quota.remaining <= 0 ? "bg-amber-400" : "bg-gradient-to-r from-[#3B66F5] to-[#8B5CF6]"}`}
                            style={{ width: `${Math.min(100, Math.round((quota.used / Math.max(1, quota.limit)) * 100))}%` }}
                        />
                    </div>
                    {quota.next_renewal && (
                        <div className="text-[10px] text-slate-400 mb-2.5">Rinnovo: {quota.next_renewal.split("-").reverse().join("/")}</div>
                    )}
                    <Link
                        to="/piano"
                        onClick={() => { trackEvent("upgrade_clicked"); mobile && setMobileOpen(false); }}
                        data-testid={`${mobile ? "mobile-" : ""}sidebar-upgrade`}
                        className="w-full h-9 flex items-center justify-center text-xs font-bold rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] text-white hover:from-[#8B5CF6] hover:to-[#7C3AED] transition-all"
                    >
                        {noPlan ? "Attiva un piano" : quota.remaining <= 0 ? "Passa a un piano superiore" : "Upgrade Piano"}
                    </Link>
                </div>
            )}
            {quota?.unlimited && (
                <div className="rounded-2xl border border-white/[0.09] bg-gradient-to-b from-[#141C33] to-[#0E1424] px-4 py-3" data-testid={`${mobile ? "mobile-" : ""}sidebar-quota`}>
                    <div className="flex items-center gap-2">
                        <Crown size={14} weight="fill" className="text-amber-400" />
                        <span className="text-xs font-bold text-white">Piano {planLabel}</span>
                    </div>
                    <div className="text-[11px] text-emerald-400 font-semibold mt-1">Analisi illimitate</div>
                </div>
            )}
            <button
                data-testid={`${mobile ? "mobile-" : ""}theme-toggle`}
                onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
                className="w-full h-9 flex items-center justify-center gap-2 text-xs font-semibold text-slate-300 hover:text-[#22D3EE] border border-white/[0.09] hover:border-[#22D3EE]/40 rounded-lg transition-colors"
            >
                {resolvedTheme === "dark"
                    ? <><SunDim size={14} weight="bold" /> Modalità chiara</>
                    : <><Moon size={14} weight="bold" /> Modalità scura</>}
            </button>
            <button
                data-testid={`${mobile ? "mobile-" : ""}logout-button`}
                onClick={() => { mobile && setMobileOpen(false); doLogout(); }}
                className="w-full h-9 flex items-center justify-center gap-2 text-xs font-semibold text-slate-300 hover:text-rose-400 border border-white/[0.09] hover:border-rose-500/30 rounded-lg transition-colors"
            >
                <SignOut size={13} weight="bold" /> Esci
            </button>
        </div>
    );

    const logo = (
        <Link to="/dashboard" className="flex items-center" data-testid="header-logo">
            <img src="/logo-full.png" alt="Asta Intelligence — Dati. Analisi. Opportunità." className="h-16 lg:h-[96px] w-auto mix-blend-screen" />
        </Link>
    );

    const userChip = (
        <Link to="/account" data-testid="nav-account" className="flex items-center gap-3 group">
            <div className="text-right leading-tight hidden md:block">
                <div className="text-[13px] font-semibold text-white group-hover:text-[#7C9AFF] transition-colors truncate max-w-[180px]">{user?.full_name || "Account"}</div>
                <div className="text-[11px] text-slate-400">{planText || user?.organization_name}</div>
            </div>
            <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3B66F5] to-[#8B5CF6] flex items-center justify-center shrink-0 border-2 border-white/10">
                <UserCircle size={22} weight="fill" className="text-white" />
            </span>
        </Link>
    );

    return (
        <div className="min-h-screen bg-[#04060B] text-slate-100">
            {trialLocked && <PlanLockOverlay onLogout={doLogout} />}
            {/* ===== Desktop sidebar ===== */}
            <aside
                className="fd hidden lg:flex fixed inset-y-0 left-0 w-[252px] flex-col bg-[#05070E] border-r border-white/[0.06] z-40"
                data-testid="app-sidebar"
            >
                <div className="px-5 h-[96px] flex items-center">{logo}</div>
                <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5">{navLinks(false)}</nav>
                <div className="px-3 pb-5 pt-3">{planCard(false)}</div>
            </aside>

            {/* ===== Mobile top bar ===== */}
            <header
                className="fd lg:hidden sticky top-0 z-50 bg-[#05070E]/95 backdrop-blur-md border-b border-white/[0.07]"
                data-testid="app-header"
            >
                <div className="px-4 h-16 flex items-center justify-between">
                    {logo}
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="mobile-header-theme-toggle"
                            onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
                            aria-label="Cambia tema"
                            className="w-10 h-10 flex items-center justify-center border border-white/10 hover:border-[#22D3EE]/50 rounded-lg transition-colors text-slate-100"
                        >
                            {resolvedTheme === "dark" ? <SunDim size={18} weight="bold" /> : <Moon size={18} weight="bold" />}
                        </button>
                        <button
                            data-testid="mobile-menu-toggle"
                            onClick={() => setMobileOpen((v) => !v)}
                            aria-label="Menu"
                            aria-expanded={mobileOpen}
                            className="w-10 h-10 flex items-center justify-center border border-white/10 hover:border-[#22D3EE]/50 rounded-lg transition-colors text-slate-100"
                        >
                            {mobileOpen ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
                        </button>
                    </div>
                </div>
                {mobileOpen && (
                    <div className="fd border-t border-white/[0.07] bg-[#05070E] px-3 py-4 space-y-1.5 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
                        {navLinks(true)}
                        <Link
                            to="/account"
                            onClick={() => setMobileOpen(false)}
                            data-testid="mobile-nav-account"
                            className="px-4 h-11 flex items-center gap-3 text-[13px] font-semibold rounded-xl text-slate-300 hover:text-white hover:bg-white/[0.05]"
                        >
                            <UserCircle size={18} weight="regular" /> Account
                        </Link>
                        <div className="h-px bg-white/[0.07] my-3" />
                        {planCard(true)}
                    </div>
                )}
            </header>

            {/* ===== Main column ===== */}
            <div className="lg:pl-[252px] flex flex-col min-h-screen">
                {/* Desktop topbar with greeting */}
                <div className="hidden lg:flex items-center justify-between h-[76px] px-10 border-b border-white/[0.05]" data-testid="app-topbar">
                    <div className="leading-tight">
                        <div className="text-lg font-bold text-white font-display">Bentornato, {firstName} <span className="align-middle">👋</span></div>
                        <div className="text-xs text-slate-400 mt-0.5">Ecco le migliori opportunità di investimento per te oggi.</div>
                    </div>
                    {userChip}
                </div>
                <main className="flex-1 w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6">{children}</main>
                <footer className="border-t border-white/[0.05] mt-8" data-testid="app-footer">
                    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <div className="text-xs text-slate-400 text-center sm:text-left">
                            © {new Date().getFullYear()} DYNAMIC GROUP S.R.L.S. · Asta Intelligence
                        </div>
                        <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs">
                            {LEGAL_LINKS.map((l) => (
                                <Link key={l.to} to={l.to} className="text-slate-400 hover:text-white underline underline-offset-4">
                                    {l.label}
                                </Link>
                            ))}
                            <Link to="/account" className="text-slate-400 hover:text-white underline underline-offset-4">Account</Link>
                        </nav>
                    </div>
                </footer>
            </div>
        </div>
    );
}
